import React, { useEffect, useState } from 'react';
import { supabase } from '../../../services/supabaseClient';
import AdminService from '../../../services/adminService';
import WizardService from '../../../services/wizardService';
import { useAuth } from '../../../context/AuthContext';
import SearchableSelect from '../../../components/ui/SearchableSelect';

const statusOptions = ['requested', 'uploaded', 'review', 'approved', 'rejected'];

const DocumentsManager = () => {
  const { user, role } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissionDocs, setSubmissionDocs] = useState([]); // Kept for legacy compatibility if needed, but main list is 'documents'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ title: '', description: '', required: true, file_type: 'pdf', example_link: '' });
  const [pathways, setPathways] = useState([]);
  const [downloadLinks, setDownloadLinks] = useState({});
  const [uploadStates, setUploadStates] = useState({}); // { [docId]: { uploading: bool, progress: number } }
  const [renameDrafts, setRenameDrafts] = useState({}); // { [docId]: string }

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterCase, setFilterCase] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilterSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Pagination & Data
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Dropdown Data
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]); // For SearchableSelect
  const [cases, setCases] = useState([]);
  const [uploadCases, setUploadCases] = useState([]); // Specifically for upload modal

  // Manual Upload State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    clientId: '',
    caseId: '',
    taskId: '',
    appId: '',
    title: '',
    file: null
  });
  const [uploadTasks, setUploadTasks] = useState([]);
  const [uploadApps, setUploadApps] = useState([]);
  const [isUploadingManual, setIsUploadingManual] = useState(false);

  const [noteDrafts, setNoteDrafts] = useState({});
  const [autoAssigning, setAutoAssigning] = useState(null);
  const [autoAssignNotes, setAutoAssignNotes] = useState({});
  const [autoAssignErrors, setAutoAssignErrors] = useState({});
  const clipboardAvailable = typeof navigator !== 'undefined' && !!navigator.clipboard && typeof navigator.clipboard.writeText === 'function';
  const isAdmin = role === 'admin';

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      let targetSubmissionId = undefined;
      if (filterCase) {
        const c = cases.find(x => x.id === filterCase);
        if (c?.submission_id) targetSubmissionId = c.submission_id;
      } else if (filterClient) {
        // filterClient is the submission ID (lead ID)
        targetSubmissionId = filterClient;
      }

      const { rows, count } = await AdminService.getDocumentsPaged({
        page,
        pageSize,
        status: filterStatus,
        search: filterSearch,
        submissionId: targetSubmissionId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined
      });

      setDocuments(rows || []);
      setTotalCount(count || 0);

      // Update note drafts for the fetched docs
      const draftMap = {};
      (rows || []).forEach((doc) => {
        draftMap[doc.id] = doc.notes || '';
      });
      setNoteDrafts(prev => ({ ...prev, ...draftMap }));

    } catch (e) {
      setError('Failed to load documents');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInit = async () => {
      try {
        const { data: tpl } = await supabase.from('document_templates').select('*').order('sort_order');
        setTemplates(tpl || []);

        const { data: p } = await supabase.from('immigration_pathways').select('id, pathway_key').order('pathway_key');
        setPathways(p || []);

        // Load clients (leads) for filter
        const { rows: leads } = await AdminService.getWizardSubmissionsPaged({ page: 1, pageSize: 1000 }); // Fetch enough for dropdown
        setClients(leads || []);
        setSubmissions(leads || []);

        // Load cases for filter
        const { rows: caseList } = await AdminService.getCasesPaged({ page: 1, pageSize: 1000 });
        setCases(caseList || []);

      } catch (e) {
        console.error(e);
      }
    };
    loadInit();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [page, pageSize, filterStatus, filterClient, filterCase, dateFrom, dateTo]);

  // Update selectedSubmission when filterClient changes
  useEffect(() => {
    if (filterClient) {
      const sub = clients.find(c => c.id === filterClient);
      setSelectedSubmission(sub || null);
    } else {
      setSelectedSubmission(null);
    }
  }, [filterClient, clients]);

  // Fetch tasks when caseId changes for upload form
  useEffect(() => {
    const fetchTasks = async () => {
      if (!uploadForm.caseId) {
        setUploadTasks([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('case_tasks')
          .select('id, title')
          .eq('case_id', uploadForm.caseId)
          .neq('status', 'completed');
        if (error) throw error;
        setUploadTasks(data || []);
      } catch (e) {
        console.error('Error fetching tasks:', e);
      }
    };
    fetchTasks();
  }, [uploadForm.caseId]);

  // Fetch apps when clientId changes for upload form
  useEffect(() => {
    const fetchApps = async () => {
      if (!uploadForm.clientId) {
        setUploadApps([]);
        return;
      }
      try {
        const [visaRes, uniRes] = await Promise.all([
          supabase.from('visa_applications').select('id, visa_type').eq('client_id', uploadForm.clientId),
          supabase.from('uni_applications').select('id, university_name, program_name').eq('client_id', uploadForm.clientId)
        ]);

        const apps = [];
        if (visaRes.data) apps.push(...visaRes.data.map(a => ({ id: a.id, type: 'Visa', label: `${a.visa_type} Visa` })));
        if (uniRes.data) apps.push(...uniRes.data.map(a => ({ id: a.id, type: 'Uni', label: `${a.university_name} - ${a.program_name}` })));

        setUploadApps(apps);
      } catch (e) {
        console.error('Error fetching apps:', e);
      }
    };
    fetchApps();

    // Fetch cases for upload form when client changes
    const fetchUploadCases = async () => {
      if (!uploadForm.clientId) {
        setUploadCases([]);
        return;
      }
      try {
        // Fetch ALL cases for this client, not just paged
        const { rows } = await AdminService.getCasesPaged({
          clientId: uploadForm.clientId,
          pageSize: 100
        });
        setUploadCases(rows || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchUploadCases();

  }, [uploadForm.clientId]);

  // Handle Client Search
  const handleClientSearch = async (searchTerm) => {
    if (!searchTerm) {
      setFilteredClients(clients.slice(0, 50).map(c => ({
        value: c.id,
        label: c.personal_info?.fullName || `${c.first_name} ${c.last_name}`
      })));
      return;
    }

    // Server-side search if needed, or filter local if we have enough data (we only have 1000)
    // Actually, AdminService.getWizardSubmissionsPaged supports search.
    try {
      const { rows } = await AdminService.getWizardSubmissionsPaged({
        search: searchTerm,
        page: 1,
        pageSize: 20
      });
      setFilteredClients((rows || []).map(c => ({
        value: c.id,
        label: c.personal_info?.fullName || `${c.first_name} ${c.last_name}`
      })));
    } catch (e) {
      console.error(e);
    }
  };

  // Initialize Filtered Clients
  useEffect(() => {
    if (clients.length) {
      setFilteredClients(clients.slice(0, 50).map(c => ({
        value: c.id,
        label: c.personal_info?.fullName || `${c.first_name} ${c.last_name}`
      })));
    }
  }, [clients]);

  // Helper functions
  const statusChipClass = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-900/30 text-green-400 border border-green-800';
      case 'rejected': return 'bg-red-900/30 text-red-400 border border-red-800';
      case 'uploaded': return 'bg-indigo-900/30 text-indigo-400 border border-indigo-800';
      case 'review': return 'bg-amber-900/30 text-amber-400 border border-amber-800';
      default: return 'bg-gray-700 text-gray-400 border border-gray-600';
    }
  };

  const getDocumentPath = (doc) => {
    if (!doc) return null;
    if (doc.file_path) return doc.file_path;
    if (!doc.file_url) return null;
    try {
      const url = new URL(doc.file_url);
      const decoded = decodeURIComponent(url.pathname);
      const marker = '/documents/';
      const publicMarker = '/object/public/documents/';
      if (decoded.includes(publicMarker)) return decoded.split(publicMarker)[1];
      if (decoded.includes(marker)) return decoded.split(marker)[1];
    } catch (e) {
      console.warn('Failed to derive storage path', e);
    }
    return null;
  };

  const ensureSignedUrl = async (doc) => {
    if (!canReviewDoc(doc)) throw new Error('Only the assigned reviewer or an admin can access this file.');
    const path = getDocumentPath(doc);
    const existing = downloadLinks[doc.id];
    if (existing && existing.expiresAt && existing.expiresAt > Date.now() + 5000) return existing.url;
    if (!path) return doc.file_url || null;

    try {
      const { data, error } = await supabase.storage.from('documents').createSignedUrl(path, 600);
      if (error) throw error;
      const signedUrl = data?.signedUrl || null;
      if (signedUrl) {
        setDownloadLinks((prev) => ({
          ...prev,
          [doc.id]: { url: signedUrl, expiresAt: Date.now() + 600 * 1000 }
        }));
        return signedUrl;
      }
    } catch (e) {
      console.warn('Signed URL generation failed', e);
      throw e;
    }
  };

  const openDocument = async (doc) => {
    try {
      const url = await ensureSignedUrl(doc);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else alert('Document link unavailable');
    } catch (err) {
      alert(err?.message || 'You do not have permission to open this document.');
    }
  };

  const copyDocumentLink = async (doc) => {
    if (!canReviewDoc(doc)) { alert('Only the assigned reviewer or an admin can copy this link.'); return; }
    const url = await ensureSignedUrl(doc);
    if (!url) { alert('Document link unavailable'); return; }
    if (!clipboardAvailable) { alert('Secure link generated. Open the document to copy the URL manually.'); return; }
    try {
      await navigator.clipboard.writeText(url);
      alert('Secure link copied. This link expires in 10 minutes.');
    } catch (e) {
      console.warn('Clipboard copy failed', e);
      alert('Unable to copy link. Please open the document and copy manually.');
    }
  };

  const linkExpiresIn = (docId) => {
    const entry = downloadLinks[docId];
    if (!entry || !entry.expiresAt) return null;
    const remainingMs = entry.expiresAt - Date.now();
    if (remainingMs <= 0) return null;
    const mins = Math.ceil(remainingMs / 60000);
    return `${mins} min`;
  };

  const getHistory = (doc) => {
    const meta = doc?.metadata;
    if (!meta) return [];
    const history = meta.review_history || meta.reviewHistory || [];
    return Array.isArray(history) ? history : [];
  };

  const appendHistory = (doc, entry) => {
    const existing = getHistory(doc);
    return [
      { ...entry, at: entry.at || new Date().toISOString(), by: entry.by || user?.email || user?.id || null },
      ...existing
    ];
  };

  const describeHistoryEntry = (entry) => {
    if (!entry) return 'No activity yet';
    const parts = [];
    if (entry.action) parts.push(entry.action.replace(/_/g, ' '));
    if (entry.status) parts.push(`→ ${entry.status}`);
    if (entry.note) parts.push(`— ${entry.note}`);
    return parts.join(' ').trim() || 'No activity yet';
  };

  const isAssignedToCurrentUser = (doc) => !!doc?.reviewed_by && doc.reviewed_by === user?.id;
  const isUnassigned = (doc) => !doc?.reviewed_by;
  const canReviewDoc = (doc) => isAdmin || isAssignedToCurrentUser(doc);
  const canModifyDoc = (doc) => canReviewDoc(doc) || (isUnassigned(doc) && role === 'docs');

  const buildMetadata = (doc, patch = {}, history) => {
    const base = doc?.metadata && typeof doc.metadata === 'object' ? doc.metadata : {};
    const next = { ...base, ...patch };
    if (history) next.review_history = history;
    return next;
  };

  const requestDoc = async (tpl) => {
    if (!selectedSubmission) {
      alert('Please select a client first using the filter.');
      return;
    }
    try {
      const history = appendHistory({}, { action: 'requested', status: 'requested', note: `Requested template ${tpl.title}` });
      const metadata = buildMetadata({}, { requested_by: user?.email || user?.id || null, reviewer: null }, history);
      const { data: inserted, error } = await supabase.from('submission_documents').insert([{
        submission_id: selectedSubmission.id,
        template_id: tpl.id,
        title: tpl.title,
        status: 'requested',
        metadata,
        client_id: selectedSubmission.id // Assuming client_id is same as submission_id for now, or we should fetch it. 
        // Wait, submission_id links to wizard_submissions. client_id is also on submission_documents.
        // In wizard_submissions, id is the client id? Yes.
      }]).select().single();

      if (error) throw error;
      fetchDocuments(); // Refresh list
      await AdminService.recordAuditLog('submission_document_requested', {
        entity: 'submission_document',
        entityId: inserted?.id || null,
        summary: `Requested ${tpl.title}`,
        metadata: { submission_id: selectedSubmission.id, template_id: tpl.id, template_title: tpl.title },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      alert('Failed to request document');
      console.error(e);
    }
  };

  const updateDocStatus = async (docId, status) => {
    try {
      const current = documents.find((d) => d.id === docId) || {};
      if (!canReviewDoc(current)) { alert('Only the assigned reviewer or an admin can update document status.'); return; }

      const history = appendHistory(current, { action: 'status_change', from: current.status || null, status, note: current.notes || null });
      const updates = { status };
      if (['approved', 'rejected'].includes(status)) {
        updates.reviewed_by = user?.id || null;
        updates.reviewed_at = new Date().toISOString();
      }
      if (['requested', 'uploaded', 'review'].includes(status)) {
        updates.reviewed_at = null;
      }
      updates.metadata = buildMetadata(current, {}, history);

      await supabase.from('submission_documents').update(updates).eq('id', docId);
      fetchDocuments();

      await AdminService.recordAuditLog('submission_document_status_change', {
        entity: 'submission_document',
        entityId: docId,
        summary: `Status ${current?.status || 'unknown'} → ${status}`,
        metadata: { submission_id: current.submission_id, previous_status: current?.status || null, new_status: status },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      alert('Failed to update status');
    }
  };

  const uploadDocFile = async (docId, file) => {
    if (!file) return;

    // 1. Validation limits
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'text/plain'
    ];

    try {
      const current = documents.find((d) => d.id === docId);
      if (!current) throw new Error('Document record not found');

      // 2. Permission check
      if (!canReviewDoc(current)) {
        alert('Only the assigned reviewer or an admin can upload files.');
        return;
      }

      // 3. File validation
      if (file.size > MAX_SIZE) {
        throw new Error(`File is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Max size is 10MB.`);
      }
      // Optional: Strict type check (can be lenient if needed, but good for security)
      // if (!ALLOWED_TYPES.includes(file.type)) {
      //   throw new Error('Invalid file type. Allowed: PDF, DOC/DOCX, JPG, PNG, TXT');
      // }

      // 4. Safe path construction
      if (!current.submission_id) {
        throw new Error('Missing Submission ID. Cannot construct safe upload path.');
      }

      const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
      // Sanitize filename to avoid path issues
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const path = `submissions/${current.submission_id}/docs/${docId}/${Date.now()}_${safeName}`;

      setUploadStates(prev => ({ ...prev, [docId]: { uploading: true, progress: 0 } }));

      const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { upsert: true });
      if (upErr) throw upErr;

      const { data: pub } = await supabase.storage.from('documents').getPublicUrl(path);
      const publicUrl = pub?.publicUrl || null;

      const history = appendHistory(current, { action: 'file_uploaded', status: current.status || 'requested', note: file.name });

      const { error: dbErr } = await supabase.from('submission_documents').update({
        file_url: publicUrl,
        file_path: path,
        status: 'uploaded',
        uploaded_at: new Date().toISOString(),
        metadata: buildMetadata(current, {}, history)
      }).eq('id', docId);

      if (dbErr) throw dbErr;

      setUploadStates(prev => ({ ...prev, [docId]: { uploading: false, progress: 100 } }));
      fetchDocuments();

      await AdminService.recordAuditLog('submission_document_uploaded', {
        entity: 'submission_document',
        entityId: docId,
        summary: `Uploaded file ${file.name}`,
        metadata: { submission_id: current.submission_id, filename: file.name, storage_path: path },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      console.error('Upload error', e);
      alert(`Failed to upload file: ${e.message}`);
      setUploadStates(prev => ({ ...prev, [docId]: { uploading: false, progress: 0 } }));
    }
  };

  const deleteDocFile = async (docId) => {
    const current = documents.find((d) => d.id === docId);
    if (!current) return;
    if (!canReviewDoc(current)) { alert('Only the assigned reviewer or an admin can delete files.'); return; }

    const path = getDocumentPath(current) || current.file_path;
    if (!path) { alert('No file to delete'); return; }

    if (!window.confirm(`Delete file for "${current.title}"?`)) return;

    try {
      const { error } = await supabase.storage.from('documents').remove([path]);
      if (error) throw error;

      const history = appendHistory(current, { action: 'file_deleted', status: current.status || 'uploaded', note: path });

      await supabase.from('submission_documents').update({
        file_url: null,
        file_path: null,
        status: 'requested',
        metadata: buildMetadata(current, {}, history)
      }).eq('id', docId);

      fetchDocuments();

      await AdminService.recordAuditLog('submission_document_deleted', {
        entity: 'submission_document',
        entityId: docId,
        summary: `Deleted document file`,
        metadata: { storage_path: path },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      console.error('Delete error', e);
      alert('Failed to delete file');
    }
  };

  const renameDocFile = async (docId) => {
    const current = documents.find((d) => d.id === docId);
    if (!current) return;
    if (!canReviewDoc(current)) { alert('Only the assigned reviewer or an admin can rename files.'); return; }

    const path = getDocumentPath(current) || current.file_path;
    const draftName = (renameDrafts[docId] || '').trim();
    if (!path || !draftName) { alert('Provide a new file name'); return; }

    try {
      const baseDir = path.slice(0, path.lastIndexOf('/'));
      const newPath = `${baseDir}/${draftName.replace(/[\\/]/g, '-')}`;
      const { error } = await supabase.storage.from('documents').move(path, newPath);
      if (error) throw error;

      const { data: pub } = await supabase.storage.from('documents').getPublicUrl(newPath);
      const history = appendHistory(current, { action: 'file_renamed', status: current.status || 'uploaded', note: `${path.split('/').pop()} → ${draftName}` });

      await supabase.from('submission_documents').update({
        file_url: pub?.publicUrl || null,
        file_path: newPath,
        metadata: buildMetadata(current, {}, history)
      }).eq('id', docId);

      fetchDocuments();
      setRenameDrafts(prev => ({ ...prev, [docId]: '' }));

      await AdminService.recordAuditLog('submission_document_renamed', {
        entity: 'submission_document',
        entityId: docId,
        summary: `Renamed document file`,
        metadata: { old_path: path, new_path: newPath },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      console.error('Rename error', e);
      alert('Failed to rename file');
    }
  };

  const assignReviewer = async (doc, reviewerMeta) => {
    if (!doc?.id) return;
    if (!isAdmin && !canModifyDoc(doc)) { alert('Only the assigned reviewer or an admin can change reviewers.'); return; }

    try {
      const history = appendHistory(doc, { action: 'reviewer_assigned', status: doc.status, note: reviewerMeta?.email || reviewerMeta?.id || 'unassigned' });
      const metadata = buildMetadata(doc, { reviewer: reviewerMeta }, history);

      await supabase.from('submission_documents').update({ reviewed_by: reviewerMeta?.id || null, metadata }).eq('id', doc.id);
      fetchDocuments();

      await AdminService.recordAuditLog('submission_document_reviewer', {
        entity: 'submission_document',
        entityId: doc.id,
        summary: reviewerMeta ? `Assigned reviewer ${reviewerMeta.email || reviewerMeta.id}` : 'Reviewer cleared',
        metadata: { reviewer: reviewerMeta },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      alert('Failed to update reviewer');
    }
  };

  const handleAssignToMe = (doc) => {
    if (!user) { alert('You need to be logged in to assign reviewer'); return; }
    if (!canModifyDoc(doc) && !isAdmin) { alert('Document is assigned to another reviewer.'); return; }
    assignReviewer(doc, { id: user.id, email: user.email || null, name: user.user_metadata?.full_name || user.user_metadata?.name || user.email || null });
  };

  const handleClearReviewer = (doc) => {
    if (!isAdmin && !isAssignedToCurrentUser(doc)) { alert('Only the assigned reviewer or an admin can clear reviewers.'); return; }
    assignReviewer(doc, null);
  };

  const handleAutoAssignReviewer = async (doc) => {
    if (!doc?.id) return;
    if (!isAdmin) { alert('Only administrators can auto-assign reviewers.'); return; }

    try {
      setAutoAssignErrors((prev) => ({ ...prev, [doc.id]: null }));
      setAutoAssigning(doc.id);
      const staff = await AdminService.autoAssignStaff({ role: 'docs', team: doc?.metadata?.team || null });
      if (!staff?.email || !staff?.auth_user_id) throw new Error('No documentation specialist available for auto-assign');

      await assignReviewer(doc, {
        id: staff.auth_user_id || staff.id || null,
        email: staff.email,
        name: staff.name || staff.email,
        role: staff.role || 'docs',
        team: staff.team || null
      });

      setAutoAssignNotes((prev) => ({ ...prev, [doc.id]: `Assigned to ${staff.name || staff.email} (${staff.open_cases ?? 0} open docs)` }));
    } catch (err) {
      console.error('Auto assign reviewer error', err);
      setAutoAssignErrors((prev) => ({ ...prev, [doc.id]: err?.message || 'Failed to auto-assign reviewer' }));
    } finally {
      setAutoAssigning(null);
    }
  };

  const saveReviewNotes = async (doc) => {
    if (!doc?.id) return;
    const note = noteDrafts[doc.id] || '';
    if (!canReviewDoc(doc)) { alert('Only the assigned reviewer or an admin can update notes.'); return; }

    try {
      const history = appendHistory(doc, { action: 'notes_updated', status: doc.status, note });
      await supabase.from('submission_documents').update({ notes: note, metadata: buildMetadata(doc, {}, history) }).eq('id', doc.id);
      fetchDocuments();

      await AdminService.recordAuditLog('submission_document_notes', {
        entity: 'submission_document',
        entityId: doc.id,
        summary: `Updated document notes (${note.length} chars)`,
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      alert('Failed to save notes');
    }
  };

  const saveTemplate = async () => {
    try {
      if (!newTemplate.title.trim()) return alert('Title is required');
      const payload = { ...newTemplate };
      if (payload.pathway_id === '') delete payload.pathway_id;
      const { data, error } = await supabase.from('document_templates').insert([payload]).select().single();
      if (error) throw error;

      setNewTemplate({ title: '', description: '', required: true, file_type: 'pdf', example_link: '', pathway_id: '' });
      const { data: tpl } = await supabase.from('document_templates').select('*').order('sort_order');
      setTemplates(tpl || []);

      await AdminService.recordAuditLog('document_template_created', {
        entity: 'document_template',
        entityId: data?.id || null,
        summary: `Created template ${payload.title}`,
        metadata: payload,
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      alert('Failed to save template');
    }
  };

  const toggleTemplate = async (tpl, field) => {
    try {
      const { error } = await supabase.from('document_templates').update({ [field]: !tpl[field] }).eq('id', tpl.id);
      if (error) throw error;
      const { data: t } = await supabase.from('document_templates').select('*').order('sort_order');
      setTemplates(t || []);

      await AdminService.recordAuditLog('document_template_updated', {
        entity: 'document_template',
        entityId: tpl.id,
        summary: `${field} toggled to ${!tpl[field]}`,
        metadata: { field, previous: tpl[field], current: !tpl[field] },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      alert('Failed to update template');
    }
  };

  const deleteTemplate = async (tpl) => {
    if (typeof window !== 'undefined' && !window.confirm('Delete this template?')) return;
    try {
      const { error } = await supabase.from('document_templates').delete().eq('id', tpl.id);
      if (error) throw error;
      const { data: t } = await supabase.from('document_templates').select('*').order('sort_order');
      setTemplates(t || []);

      await AdminService.recordAuditLog('document_template_deleted', {
        entity: 'document_template',
        entityId: tpl.id,
        summary: `Deleted template ${tpl.title}`,
        metadata: { title: tpl.title },
        performedBy: user?.email || user?.id || null
      });
    } catch (e) {
      alert('Failed to delete template');
    }
  };

  const handleManualUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.clientId || !uploadForm.file) {
      alert('Client and File are required');
      return;
    }

    try {
      setIsUploadingManual(true);
      const file = uploadForm.file;
      const clientId = uploadForm.clientId;
      const subId = clientId; // Since client table is wizard_submissions

      const MAX_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_SIZE) throw new Error('File too large (max 10MB)');

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      // Create a specific folder for manual uploads or keep standard structure
      // Using standard structure: submissions/:subId/docs/:docId/...
      // But we need a docId first. So we insert first or gen ID?
      // Better to insert generic record first to get ID.

      // Prepare metadata with links
      const links = {};
      if (uploadForm.caseId) links.case_id = uploadForm.caseId;
      if (uploadForm.taskId) links.task_id = uploadForm.taskId;
      if (uploadForm.appId) links.application_id = uploadForm.appId;

      const history = [{
        action: 'manually_uploaded',
        status: 'uploaded',
        at: new Date().toISOString(),
        by: user?.email || user?.id,
        note: 'Manual upload by admin'
      }];

      // 1. Insert record
      const { data: doc, error: insertErr } = await supabase.from('submission_documents').insert([{
        submission_id: subId,
        client_id: subId,
        title: uploadForm.title || file.name,
        status: 'uploaded', // Directly uploaded
        uploaded_by: user?.id,
        uploaded_at: new Date().toISOString(),
        metadata: {
          manual_upload: true,
          ...links,
          review_history: history
        }
      }]).select().single();

      if (insertErr) throw insertErr;

      // 2. Upload file
      const path = `submissions/${subId}/docs/${doc.id}/${Date.now()}_${safeName}`;
      const { error: upErr } = await supabase.storage.from('documents').upload(path, file);
      if (upErr) {
        // cleanup record if upload fails?
        await supabase.from('submission_documents').delete().eq('id', doc.id);
        throw upErr;
      }

      const { data: pub } = await supabase.storage.from('documents').getPublicUrl(path);

      // 3. Update record with file path
      const { error: updateErr } = await supabase.from('submission_documents').update({
        file_url: pub?.publicUrl || null,
        file_path: path
      }).eq('id', doc.id);

      if (updateErr) throw updateErr;

      // 4. Audit Log
      await AdminService.recordAuditLog('document_manual_upload', {
        entity: 'submission_document',
        entityId: doc.id,
        summary: `Manually uploaded ${file.name}`,
        metadata: { ...links, clientId },
        performedBy: user?.email || user?.id
      });

      alert('File uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({ clientId: '', caseId: '', taskId: '', appId: '', title: '', file: null });
      fetchDocuments();

    } catch (err) {
      console.error('Manual upload error:', err);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setIsUploadingManual(false);
    }
  };

  if (loading && !documents.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      {/* Manage Templates */}
      <div className="bg-gray-800 p-4 border border-gray-700 rounded mb-4">
        <h3 className="text-lg font-medium text-white">Manage Templates</h3>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="space-y-2">
            <input className="w-full bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400" placeholder="Title" value={newTemplate.title} onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })} />
            <textarea className="w-full bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400" placeholder="Description" value={newTemplate.description} onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })} />
            <div className="flex items-center gap-3">
              <label className="text-sm flex items-center gap-2 text-gray-300"><input type="checkbox" checked={newTemplate.required} onChange={(e) => setNewTemplate({ ...newTemplate, required: e.target.checked })} /> Required</label>
              <select className="bg-gray-700 border-gray-600 rounded px-2 py-1 text-sm text-white" value={newTemplate.file_type} onChange={(e) => setNewTemplate({ ...newTemplate, file_type: e.target.value })}>
                {['pdf', 'jpg', 'png', 'docx'].map(ft => <option key={ft} value={ft}>{ft.toUpperCase()}</option>)}
              </select>
            </div>
            <input className="w-full bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400" placeholder="Example link (optional)" value={newTemplate.example_link} onChange={(e) => setNewTemplate({ ...newTemplate, example_link: e.target.value })} />
            <select className="bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm w-full text-white" value={newTemplate.pathway_id || ''} onChange={(e) => setNewTemplate({ ...newTemplate, pathway_id: e.target.value })}>
              <option value="">All pathways</option>
              {pathways.map(p => (
                <option key={p.id} value={p.id}>
                  {WizardService.formatPathwayName(p.pathway_key)}
                </option>
              ))}
            </select>
            <button onClick={saveTemplate} className="px-3 py-1 text-sm border border-gray-600 rounded hover:bg-gray-700 text-white">Add Template</button>
          </div>
          <div className="space-y-2">
            {templates.map((tpl) => (
              <div key={tpl.id} className="p-3 border border-gray-700 rounded bg-gray-700/30">
                <div className="font-medium text-white">{tpl.title}</div>
                <div className="text-xs text-gray-400">{tpl.description}</div>
                <div className="flex items-center gap-3 mt-2">
                  <label className="text-xs flex items-center gap-1 text-gray-300"><input type="checkbox" checked={tpl.required} onChange={() => toggleTemplate(tpl, 'required')} /> Required</label>
                  <label className="text-xs flex items-center gap-1 text-gray-300"><input type="checkbox" checked={tpl.is_active} onChange={() => toggleTemplate(tpl, 'is_active')} /> Active</label>
                  <button onClick={() => deleteTemplate(tpl)} className="text-xs px-2 py-1 border border-gray-600 rounded hover:bg-gray-700 text-red-400">Delete</button>
                </div>
              </div>
            ))}
            {templates.length === 0 && (
              <div className="text-sm text-gray-400">No templates yet.</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-800 p-4 border border-gray-700 rounded mb-4">
        <h3 className="text-lg font-medium text-white">Document Templates</h3>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          {templates.map((tpl) => (
            <div key={tpl.id} className="p-3 border border-gray-700 rounded bg-gray-700/30">
              <div className="font-medium text-white">{tpl.title}</div>
              <div className="text-xs text-gray-400">{tpl.description}</div>
              <button
                disabled={!selectedSubmission}
                onClick={() => requestDoc(tpl)}
                className="mt-2 px-3 py-1 text-sm border border-gray-600 rounded hover:bg-gray-700 text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedSubmission ? `Request for ${selectedSubmission.personal_info?.fullName || 'Client'}` : 'Select a client to request'}
              </button>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="text-sm text-gray-400">No templates yet.</div>
          )}
        </div>
      </div>

      <div className="bg-gray-800 p-4 border border-gray-700 rounded space-y-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-medium text-white">Documents</h3>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload Document
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <select className="bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white" value={filterClient} onChange={(e) => setFilterClient(e.target.value)}>
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.personal_info?.fullName || `${c.first_name} ${c.last_name}`}</option>
              ))}
            </select>
            <select className="bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white" value={filterCase} onChange={(e) => setFilterCase(e.target.value)}>
              <option value="">All Cases</option>
              {cases.map(c => (
                <option key={c.id} value={c.id}>{c.title} ({c.status})</option>
              ))}
            </select>
            <select className="bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="date" className="bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <input type="date" className="bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <input
              className="bg-gray-700 border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {(filterStatus !== 'all' || filterClient || filterCase || dateFrom || dateTo || searchTerm) && (
              <button
                onClick={() => {
                  setFilterStatus('all');
                  setFilterClient('');
                  setFilterCase('');
                  setDateFrom('');
                  setDateTo('');
                  setSearchTerm('');
                  setFilterSearch('');
                }}
                className="px-3 py-2 text-xs border border-red-800 bg-red-900/30 text-red-400 rounded hover:bg-red-900/50"
              >
                Clear Filters
              </button>
            )}


            <button
              type="button"
              onClick={async (e) => {
                e.preventDefault();
                if (!window.confirm('Sync all existing CVs to Documents? This may take a moment.')) return;
                try {
                  const btn = document.getElementById('sync-cvs-btn');
                  if (btn) { btn.disabled = true; btn.textContent = 'Syncing...'; }

                  const WizardSubmissionService = (await import('../../../services/wizardSubmissionService')).default;
                  const result = await WizardSubmissionService.syncAllCVs();

                  alert(`Sync complete.\nTotal processed: ${result.total}\nSynced: ${result.synced}\nErrors: ${result.errors}`);
                  fetchDocuments();
                } catch (e) {
                  console.error(e);
                  alert('Sync failed');
                } finally {
                  const btn = document.getElementById('sync-cvs-btn');
                  if (btn) { btn.disabled = false; btn.textContent = 'Sync CVs'; }
                }
              }}
              id="sync-cvs-btn"
              className="px-3 py-2 border border-blue-600 bg-blue-900/30 text-blue-400 rounded hover:bg-blue-900/50 text-xs"
              title="Backfill missing CVs into documents list"
            >
              Sync CVs
            </button>

            <button
              onClick={fetchDocuments}
              className="p-2 border border-gray-600 rounded hover:bg-gray-700 text-white"
              title="Refresh"
            >
              🔄
            </button>
          </div>
        </div>

        {loading && <div className="linear-progress w-full h-1 bg-gray-700 overflow-hidden"><div className="h-full bg-blue-500 animate-pulse"></div></div>}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Client</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Title</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Reviewer</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Notes</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Updated</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 text-white">
              {documents.map((d) => {
                const remaining = linkExpiresIn(d.id);
                const historyItems = getHistory(d);
                const latestHistory = historyItems[0];
                const clientName = d.wizard_submissions?.personal_info?.fullName ||
                  `${d.wizard_submissions?.first_name || ''} ${d.wizard_submissions?.last_name || ''}`.trim() ||
                  'Unknown';

                return (
                  <React.Fragment key={d.id}>
                    <tr>
                      <td className="px-3 py-2 text-sm text-gray-300">{clientName}</td>
                      <td className="px-3 py-2 text-xs text-gray-400 uppercase">{d.document_type || '-'}</td>
                      <td className="px-3 py-2 text-sm font-medium">{d.title}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium ${statusChipClass(d.status)}`}>{d.status}</span>
                          <select
                            value={d.status}
                            onChange={(e) => updateDocStatus(d.id, e.target.value)}
                            disabled={!canReviewDoc(d)}
                            className="border border-gray-600 rounded px-2 py-1 text-xs bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                          >
                            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="mt-1 text-[11px] text-gray-400">
                          {describeHistoryEntry(latestHistory)}
                          {latestHistory?.at && (
                            <span className="text-gray-500"> · {new Date(latestHistory.at).toLocaleString()}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        <div className="space-y-2">
                          <div className="text-xs">
                            {d.metadata?.reviewer?.email || d.metadata?.reviewer?.name || d.reviewed_by || 'Unassigned'}
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => handleAssignToMe(d)}
                              disabled={!isAdmin && !isUnassigned(d) && !isAssignedToCurrentUser(d)}
                              className="px-2 py-1 text-[11px] border border-gray-600 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
                            >Assign to me</button>
                            <button
                              type="button"
                              onClick={() => handleClearReviewer(d)}
                              disabled={!isAdmin && !isAssignedToCurrentUser(d)}
                              className="px-2 py-1 text-[11px] border border-gray-600 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
                            >Clear</button>
                            <button
                              type="button"
                              disabled={autoAssigning === d.id || !isAdmin || !isUnassigned(d)}
                              onClick={() => handleAutoAssignReviewer(d)}
                              className="px-2 py-1 text-[11px] border border-blue-800 rounded bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 disabled:opacity-50"
                            >{autoAssigning === d.id ? 'Auto assigning…' : 'Auto assign'}</button>
                          </div>
                          {autoAssignNotes[d.id] && (
                            <div className="text-[11px] text-green-400">{autoAssignNotes[d.id]}</div>
                          )}
                          {autoAssignErrors[d.id] && (
                            <div className="text-[11px] text-red-400">{autoAssignErrors[d.id]}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="space-y-2">
                          <textarea
                            value={noteDrafts[d.id] || ''}
                            onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [d.id]: e.target.value }))}
                            placeholder="Add review notes"
                            disabled={!canReviewDoc(d)}
                            className="w-full border border-gray-600 bg-gray-700 rounded px-2 py-1 text-xs text-white disabled:bg-gray-800 disabled:text-gray-500"
                            rows={3}
                          />
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => saveReviewNotes(d)}
                              disabled={!canReviewDoc(d)}
                              className="px-2 py-1 text-[11px] border border-gray-600 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
                            >Save Notes</button>
                            <span className="text-[11px] text-gray-500">{(noteDrafts[d.id] || '').length} chars</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">{new Date(d.updated_at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-col gap-2 items-end">
                          <div className="flex flex-wrap items-center gap-2 justify-end">
                            <input type="file" onChange={(e) => uploadDocFile(d.id, e.target.files?.[0])} className="text-xs w-48" disabled={!canReviewDoc(d)} />
                            {uploadStates[d.id]?.uploading && (
                              <span className="text-[11px] text-gray-500">Uploading… {uploadStates[d.id]?.progress ?? 0}%</span>
                            )}
                            <button
                              type="button"
                              onClick={() => openDocument(d)}
                              disabled={(!d.file_url && !d.file_path) || !canReviewDoc(d)}
                              className="px-2 py-1 text-[11px] border border-gray-600 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
                            >Open</button>
                            <button
                              type="button"
                              onClick={() => copyDocumentLink(d)}
                              disabled={(!d.file_url && !d.file_path) || !clipboardAvailable || !canReviewDoc(d)}
                              className="px-2 py-1 text-[11px] border border-gray-600 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50"
                            >Copy Link</button>
                          </div>
                          {(d.file_url || d.file_path) && canReviewDoc(d) && (
                            <div className="flex gap-2 justify-end">
                              <input placeholder="Rename file"
                                value={renameDrafts[d.id] || ''}
                                onChange={(e) => setRenameDrafts(prev => ({ ...prev, [d.id]: e.target.value }))}
                                className="text-[11px] border border-gray-600 bg-gray-700 text-white rounded px-2 py-1 w-32" />
                              <button type="button" onClick={() => renameDocFile(d.id)} className="px-2 py-1 text-[11px] border border-gray-600 rounded bg-gray-700 hover:bg-gray-600 text-white">Save</button>
                              <button type="button" onClick={() => deleteDocFile(d.id)} className="px-2 py-1 text-[11px] border border-gray-600 rounded bg-gray-700 hover:bg-gray-600 text-white">Delete</button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr className="bg-gray-800/50">
                      <td colSpan={7} className="px-3 py-2">
                        <details>
                          <summary className="text-xs text-gray-400 cursor-pointer">Review history</summary>
                          <div className="mt-2 space-y-2">
                            {historyItems.length === 0 && (
                              <div className="text-[11px] text-gray-500">No history yet.</div>
                            )}
                            {historyItems.map((entry, idx) => (
                              <div key={idx} className="text-[11px] text-gray-400 flex justify-between">
                                <span>{describeHistoryEntry(entry)}</span>
                                <span className="text-gray-400">{entry.at ? new Date(entry.at).toLocaleString() : ''}{entry.by ? ` · ${entry.by}` : ''}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {documents.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">No documents found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-between items-center mt-4 text-sm text-gray-400">
        <div>
          Showing {documents.length} of {totalCount} documents
        </div>
        <div className="flex gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1 border border-gray-600 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="flex items-center px-2">
            Page {page}
          </span>
          <button
            disabled={page * pageSize >= totalCount}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 border border-gray-600 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
      {/* Manual Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-lg shadow-xl">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-white">Upload Document</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-white"
              >✕</button>
            </div>
            <form onSubmit={handleManualUpload} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Client <span className="text-red-400">*</span></label>
                <SearchableSelect
                  options={filteredClients}
                  value={uploadForm.clientId}
                  onChange={(val) => setUploadForm({ ...uploadForm, clientId: val, caseId: '', taskId: '', appId: '' })}
                  placeholder="Search Client..."
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Link to Case (Optional)</label>
                <select
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  value={uploadForm.caseId}
                  onChange={(e) => setUploadForm({ ...uploadForm, caseId: e.target.value, taskId: '' })}
                  disabled={!uploadForm.clientId}
                >
                  <option value="">None</option>
                  {uploadCases.map(c => (
                    <option key={c.id} value={c.id}>{c.title} ({c.status})</option>
                  ))}
                </select>
              </div>

              {uploadForm.caseId && uploadTasks.length > 0 && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Link to Task (Optional)</label>
                  <select
                    className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                    value={uploadForm.taskId}
                    onChange={(e) => setUploadForm({ ...uploadForm, taskId: e.target.value })}
                  >
                    <option value="">None</option>
                    {uploadTasks.map(t => (
                      <option key={t.id} value={t.id}>{t.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {uploadApps.length > 0 && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Link to Application (Optional)</label>
                  <select
                    className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                    value={uploadForm.appId}
                    onChange={(e) => setUploadForm({ ...uploadForm, appId: e.target.value })}
                  >
                    <option value="">None</option>
                    {uploadApps.map(a => (
                      <option key={a.id} value={a.id}>{a.label} ({a.type})</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-1">Document Title (Optional)</label>
                <input
                  type="text"
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="e.g. Signed Contract"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">File <span className="text-red-400">*</span></label>
                <input
                  type="file"
                  required
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white"
                  disabled={isUploadingManual}
                >Cancel</button>
                <button
                  type="submit"
                  disabled={isUploadingManual || !uploadForm.clientId || !uploadForm.file}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 flex items-center gap-2"
                >
                  {isUploadingManual ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div >
  );
};

export default DocumentsManager;
